/**
 * Entity cool-off (CS511 lever 3, CS512): sales puts an entity that declined
 * paid coverage into cool-off; its masthead-caps merit is penalised by
 * −30 × 0.5^(elapsed / 45 d) while active, ending at 180 days.
 * Backend: coverage-routing's `api/admin/cooloff.py` (role `sales`; design
 * docs/wip/assayer-stream/ENTITY_COOLOFF_DESIGN_CS511_2026-10-31.md §4).
 */
import { apiClient } from './config.js';

/** Mirrors the backend's cool-off states (design §2). */
export type CooloffState = 'active' | 'paused' | 'expired';

/** `add` on a paused row resumes it; on an expired or missing row it starts fresh. */
export type CooloffAction = 'add' | 'remove' | 'reset';

/** Mirrors the backend's event actions; `readd` and `expire` are written by the backend. */
export type CooloffEventAction = 'add' | 'remove' | 'readd' | 'reset' | 'expire';

export interface CooloffItem {
  entity_id: string;
  entity_name: string;
  ticker: string | null;
  state: CooloffState;
  /** Current penalty; 0 unless active. */
  weight: number;
  /** The kept penalty a paused row resumes at. */
  weight_if_active: number;
  elapsed_days: number;
  /** Null when expired. */
  next_halving_in_days: number | null;
  expires_in_days: number;
  reason: string | null;
  updated_by: string | null;
  updated_at: string | null;
}

export interface CooloffList {
  items: CooloffItem[];
  full_penalty: number;
  half_life_days: number;
  expiry_days: number;
}

export interface CooloffEvent {
  action: CooloffEventAction;
  elapsed_days_before: number | null;
  actor: string | null;
  reason: string | null;
  at: string;
}

export async function listCooloff(): Promise<CooloffList> {
  const { data } = await apiClient.get<CooloffList>('/admin/cooloff');
  return data;
}

export async function actOnCooloff(
  entityId: string,
  action: CooloffAction,
  reason?: string,
): Promise<CooloffItem> {
  const { data } = await apiClient.post<CooloffItem>(`/admin/cooloff/${entityId}`, {
    action,
    reason: reason?.trim() || undefined,
  });
  return data;
}

export async function listCooloffEvents(entityId: string): Promise<CooloffEvent[]> {
  const { data } = await apiClient.get<{ events: CooloffEvent[] }>(`/admin/cooloff/${entityId}/events`);
  return data.events;
}
